// src/components/MobileHeader.js
import React, { useState, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";
import heritageLogo from "../assets/images/Geritage.png"; // Logo
import "../styles/MobileHeader.css"; // Archivo CSS

// Importar las secciones móviles propias (páginas independientes)
import MobileHero from "../mobile/MobileHero";
import MobileHeritage from "../mobile/MobileHeritage";
import MobileReservation from "../mobile/MobileReservation";
import MobileLocation from "../mobile/MobileLocation";
import MobileServices from "../mobile/MobileServices";
import MobileReviews from "../mobile/MobileReviews";

// Drawers para acciones rápidas
import ServicesDrawer from "./ServicesDrawer";
import LocationDrawer from "./LocationDrawer";
import ContactsDrawer from "./ContactsDrawer";

// Reseñas (GoogleReviews) se muestran si se abre el drawer de Contact
import GoogleReviews from "../components/GoogleReviews";

const MobileHeader = ({ renderSections = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showServicesDrawer, setShowServicesDrawer] = useState(false);
  const [showLocationDrawer, setShowLocationDrawer] = useState(false);
  const [showContactsDrawer, setShowContactsDrawer] = useState(false);
  const [animateHeader, setAnimateHeader] = useState(false);

  useEffect(() => {
    // Activar animación al montar el componente
    setAnimateHeader(true);
  }, []);

  const handleMenuToggle = () => setMenuOpen(!menuOpen);

  // Elementos del menú: si tiene 'action', abre un drawer; si tiene 'path', navega.
  // Se añade la clase 'locationLink' al nodo de Location para darle un estilo especial.
  const navItems = [
    { label: "Heritage", path: "/" },
    { label: "Services", action: () => setShowServicesDrawer(true) },
    { label: "Location", action: () => setShowLocationDrawer(true), extraClass: "locationLink" },
    { label: "Contact", action: () => setShowContactsDrawer(true) },
  ];

  return (
    <div style={mobileHeaderStyles.pageContainer}>
      <style>{`
          /* HEADER */
          .mobileHeader {
            background-color: #9AA6B2;
            position: fixed;
            top: 0;
            width: 100%;
            padding: 10px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            z-index: 100;
            box-sizing: border-box;
            transform: translateY(-100%);
            animation: slideDown 0.8s forwards;
            font-family: 'Playfair Display', serif;
          }
          .mobileHeader.animate {
            transform: translateY(0);
          }
          @keyframes slideDown {
            from { transform: translateY(-100%); }
            to { transform: translateY(0); }
          }
          .logoSection {
            display: flex;
            align-items: center;
            gap: 5px;
          }
          .menuIconContainer {
            cursor: pointer;
            transition: transform 0.3s ease;
          }
          .menuIconContainer:hover {
            transform: scale(1.1);
          }
          .menuIcon, .closeIcon {
            color: #0d2b3e;
            font-size: 1.8rem;
          }
          .mobileNav.floatingNav {
            background-color: #9AA6B2;
            position: fixed;
            top: 60px;
            width: 100%;
            padding: 10px 0;
            z-index: 99;
            box-sizing: border-box;
          }
          .mobileNav.floatingNav .navLink {
            color: #0d2b3e;
            padding: 10px 20px;
            display: block;
            text-align: center;
            font-weight: bold;
            font-family: 'Playfair Display', serif;
            letter-spacing: 1px;
            text-transform: uppercase;
            transition: background-color 0.3s ease, transform 0.3s ease;
          }
          .mobileNav.floatingNav .navLink:hover {
            background-color: rgba(13, 43, 62, 0.1);
            transform: translateY(-2px);
          }
          /* Estilo especial para el nodo de Location */
          .mobileNav.floatingNav .locationLink {
            border: 2px solid #0d2b3e;
            border-radius: 5px;
          }
          @media (max-width: 480px) {
            .mobileNav.floatingNav .navLink {
              padding: 8px 10px;
              font-size: 0.9rem;
            }
          }
      `}</style>

      {/* HEADER: logo a la izquierda y menú a la derecha */}
      <header className={`mobileHeader ${animateHeader ? "animate" : ""}`}>
        <div className="logoSection" style={mobileHeaderStyles.logoSection}>
          <img
            src={heritageLogo}
            alt="Heritage Car Airport Logo"
            style={mobileHeaderStyles.logo}
          />
          <div className="textContainer" style={mobileHeaderStyles.textContainer}>
            <h1 className="logoText" style={mobileHeaderStyles.logoText}>Heritage</h1>
            <h2 className="logoSubText" style={mobileHeaderStyles.logoSubText}>Car Airport</h2>
          </div>
        </div>
        <div className="menuIconContainer" onClick={handleMenuToggle}>
          {menuOpen ? <FaTimes className="closeIcon" /> : <FaBars className="menuIcon" />}
        </div>
        {menuOpen && (
          <nav className="mobileNav floatingNav">
            {navItems.map((item, index) =>
              item.action ? (
                <button
                  key={index}
                  onClick={() => {
                    item.action();
                    handleMenuToggle();
                  }}
                  className={`navLink ${item.extraClass ? item.extraClass : ""}`}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={index}
                  to={item.path}
                  onClick={handleMenuToggle}
                  className={`navLink ${item.extraClass ? item.extraClass : ""}`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
        )}
      </header>

      {/* Renderizado condicional de secciones móviles (solo si renderSections es true) */}
      {renderSections && (
        <>
          <MobileHero />
          <MobileHeritage />
          <MobileReservation />
          <MobileLocation />
          <MobileServices />
          <MobileReviews />
        </>
      )}

      {/* Drawers para acciones rápidas */}
      <ServicesDrawer isOpen={showServicesDrawer} onClose={() => setShowServicesDrawer(false)} />
      <LocationDrawer isOpen={showLocationDrawer} onClose={() => setShowLocationDrawer(false)} />
      <ContactsDrawer isOpen={showContactsDrawer} onClose={() => setShowContactsDrawer(false)} />

      {/* Mostrar reseñas dentro del drawer de Contact si se abre */}
      {showContactsDrawer && <GoogleReviews />}
    </div>
  );
};

const mobileHeaderStyles = {
  pageContainer: {
    backgroundColor: "#f8fafc",
    color: "#1f2937",
    margin: 0,
    padding: 0,
    overflowX: "hidden",
    fontFamily: "Segoe UI, Tahoma, sans-serif",
  },
  logoSection: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  logo: {
    filter: "brightness(1)",
    maxHeight: "80px",
    marginRight: "0",
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "-10px",
    lineHeight: 1.2,
  },
  logoText: {
    color: "#0d2b3e",
    fontSize: "26px",
    fontWeight: 300,
    margin: 0,
    fontFamily: "'Playfair Display', serif",
  },
  logoSubText: {
    color: "#0d2b3e",
    fontSize: "18px",
    fontWeight: 300,
    margin: 0,
    fontFamily: "'Playfair Display', serif",
  },
};

export default MobileHeader;
